'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface MetricData {
  value: string;
  label: string;
  trend: string;
}

const HeroSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeMetric, setActiveMetric] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const metrics: MetricData[] = [
    { value: '500+', label: 'Businesses Served', trend: 'Growing portfolio' },
    { value: '1000+', label: 'Projects Completed', trend: 'Diverse solutions' },
    { value: '10+', label: 'Years Experience', trend: 'Industry expertise' },
    { value: '94%', label: 'Client Satisfaction', trend: 'Consistent rating' }
  ];

  useEffect(() => {
    if (!isHydrated) return;
    
    const interval = setInterval(() => {
      setActiveMetric((prev) => (prev + 1) % metrics.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isHydrated, metrics.length]);

  return (
    <section className="relative bg-gradient-to-br from-primary via-secondary to-primary-foreground/10 text-primary-foreground overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-accent rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-brand-purple rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="inline-flex items-center space-x-2 bg-primary-foreground/10 backdrop-blur-sm px-4 py-2 rounded-full border border-primary-foreground/20">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
              <span className="text-sm font-medium">Live Client Results</span>
            </div>

            <h1 className="text-4xl lg:text-6xl font-display font-bold leading-tight">
              Transform Your Business with{' '}
              <span className="text-accent">AI-Powered</span> Solutions
            </h1>

            <p className="text-lg lg:text-xl text-primary-foreground/90 leading-relaxed">
              We turn AI complexity into business clarity. Your competitive advantage is hiding in your data—let us help you unlock it with strategic automation that scales with your ambition.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                href="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-accent text-accent-foreground rounded-lg text-lg font-semibold font-display hover:bg-accent/90 transition-all duration-300 shadow-elevation hover:shadow-lg group"
              >
                Request Quote
                <Icon name="ArrowRightIcon" size={20} className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <Link
                href="/portfolio"
                className="inline-flex items-center justify-center px-8 py-4 bg-primary-foreground/10 backdrop-blur-sm text-primary-foreground rounded-lg text-lg font-semibold font-display hover:bg-primary-foreground/20 transition-all duration-300 border border-primary-foreground/20"
              >
                View Success Stories
              </Link>
            </div>

            <div className="flex items-center space-x-6 pt-4">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-10 h-10 rounded-full border-2 border-primary bg-muted"
                  ></div>
                ))}
              </div>
              <div className="text-sm">
                <div className="font-semibold">500+ Businesses Transformed</div>
                <div className="text-primary-foreground/80">Trusted by industry leaders</div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="bg-primary-foreground/10 backdrop-blur-sm rounded-2xl p-8 border border-primary-foreground/20 shadow-elevation">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-display font-semibold">Live Performance Metrics</h3>
                  <div className="flex items-center space-x-1 text-success text-sm font-medium">
                    <Icon name="ArrowTrendingUpIcon" size={16} />
                    <span>Real-time</span>
                  </div>
                </div>

                {isHydrated && (
                  <div className="space-y-4">
                    {metrics.map((metric, index) => (
                      <div
                        key={index}
                        className={`p-4 rounded-lg transition-all duration-500 ${
                          activeMetric === index
                            ? 'bg-accent/20 border-2 border-accent scale-105' :'bg-primary-foreground/5 border border-primary-foreground/10'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-3xl font-display font-bold">{metric.value}</div>
                            <div className="text-sm text-primary-foreground/80 mt-1">{metric.label}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-success text-sm font-medium flex items-center">
                              <Icon name="ArrowUpIcon" size={14} className="mr-1" />
                              {metric.trend}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="pt-4 border-t border-primary-foreground/20">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-display font-bold">150+</div>
                      <div className="text-xs text-primary-foreground/80 mt-1">Active Projects</div>
                    </div>
                    <div>
                      <div className="text-2xl font-display font-bold">50+</div>
                      <div className="text-xs text-primary-foreground/80 mt-1">Team Members</div>
                    </div>
                    <div>
                      <div className="text-2xl font-display font-bold">24/7</div>
                      <div className="text-xs text-primary-foreground/80 mt-1">Support</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-accent rounded-full blur-2xl opacity-50"></div>
            <div className="absolute -top-4 -left-4 w-32 h-32 bg-brand-purple rounded-full blur-2xl opacity-50"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;